export interface Cache {
  has: (value: any) => boolean;
  set: (key: any, value: any) => void;
  get: (key: any) => any;
}

/**
 * Error thrown when the copier traverses deeper than the configured `maxDepth`.
 *
 * @note
 * Extends `RangeError` for backwards compatibility, since exceeding the maximum depth
 * previously surfaced as a native `RangeError` from stack exhaustion.
 */
export class MaxDepthExceededError extends RangeError {
  readonly maxDepth: number;

  constructor(maxDepth: number) {
    super(`Maximum copy depth of ${String(maxDepth)} exceeded; the value copied is nested too deeply.`);

    this.maxDepth = maxDepth;
    this.name = 'MaxDepthExceededError';
  }
}

// eslint-disable-next-line @typescript-eslint/unbound-method
const toStringFunction = Function.prototype.toString;
// eslint-disable-next-line @typescript-eslint/unbound-method
const toStringObject = Object.prototype.toString;

/**
 * Get an empty version of the object with the same prototype it has.
 */
export function getCleanClone(prototype: any): any {
  if (!prototype) {
    return Object.create(null);
  }

  const Constructor = prototype.constructor;

  if (Constructor === Object) {
    return prototype === Object.prototype ? {} : Object.create(prototype as object | null);
  }

  if (Constructor && ~toStringFunction.call(Constructor).indexOf('[native code]')) {
    try {
      return new Constructor();
    } catch {
      // Ignore
    }
  }

  return Object.create(prototype as object | null);
}

/**
 * Get the tag of the value passed, so that the correct copier can be used.
 */
export function getTag(value: any): string {
  const stringTag = value[Symbol.toStringTag];

  if (stringTag) {
    return stringTag;
  }

  const type = toStringObject.call(value);

  return type.substring(8, type.length - 1);
}
